import { useMemo } from "react";
import LogoDevSvg from "@/src/assets/logos/logo-export-dev.svg";
import LogoSvg from "@/src/assets/logos/logo-beta.svg";
import LogoSnapSvg from "@/src/assets/logos/logo-snap.svg";
import GlobalSvg from "@/src/assets/icons/icon-global-language.svg";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import DropDown from "@/src/components/atom/DropDown";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import PUBLIC_PATH from "@/src/routes/public/path";
import colorSet from "@/src/styles/color";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { styled } from "styled-components";
import { useAppDispatch } from "@/src/store";
import { useEditLanguageMutation, useSignOutMutation } from "@/src/store/apis/auth";
import { setUser } from "@/src/store/slice/auth";
import { ReactComponent as ChevronDownSvg } from "@/src/assets/icons/icon-chevron-down.svg";
import LogoutSvg from "@/src/assets/icons/icon-logout.svg";
import { DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { useSignupStatus } from "@/src/hooks/useSignupStatus";
import { persistor } from "@/src/index";
import { apiSlice } from "@/src/store/apis/baseQuery";
import { currencyApi } from "@/src/store/apis/currency";
import { resetTimer } from "@/src/store/slice/sessionTime";

const PublicPageHeader = () => {
  const { t, i18n } = useTranslation(["common"]);
  const dispatch = useAppDispatch();
  const { isFirstStepSignup, normalizedUser } = useSignupStatus();
  const location = useLocation();
  const isDevelopment = process.env.REACT_APP_ENV_MODE === "local" || process.env.REACT_APP_ENV_MODE === "dev";
  const isContactUsPage = location.pathname.startsWith("/contact-us");

  const isLogoClickable = !normalizedUser || !isFirstStepSignup;

  const [editLanguage] = useEditLanguageMutation();
  const [signOut] = useSignOutMutation();

  const userName = normalizedUser?.name || "";
  const currentLanguage = i18n.language;

  const handleLanguageChange = async (value: string) => {
    i18n.changeLanguage(value);

    if (normalizedUser) {
      try {
        const { row: updatedUser } = await editLanguage({ language: value }).unwrap();
        dispatch(setUser(updatedUser));
      } catch (error) {
        console.error("Language change failed:", error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut().unwrap();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      window.onbeforeunload = null;
      persistor.purge();
      dispatch(currencyApi.util.resetApiState());
      dispatch(apiSlice.util.resetApiState());
      dispatch(resetTimer());
      window.location.replace(PUBLIC_PATH.LOGIN);
    }
  };

  const languageLabels: Record<string, string> = {
    en: t("common:english.en"),
    ko: t("common:korean.ko"),
    ja: t("common:japanese.ja"),
    "zh-TW": t("common:chinese.zh-TW"),
    "zh-CN": t("common:chinese.zh-CN"),
  };

  const languageOptions = [
    { label: t("common:english.en"), value: "en" },
    { label: t("common:korean.ko"), value: "ko" },
    { label: t("common:japanese.ja"), value: "ja" },
    { label: t("common:chinese.zh-TW"), value: "zh-TW" },
    { label: t("common:chinese.zh-CN"), value: "zh-CN" },
  ];

  const languageDropdownContentProps = useMemo(
    () => ({ align: "start" as const, sideOffset: 10 }),
    []
  );

  const accountDropdownContentProps = useMemo(
    () => ({ align: "end" as const, sideOffset: 10 }),
    []
  );

  return (
    <Header>
      <HeaderContainer>
        <Logo
          to={normalizedUser ? EXPORTER_PRIVATE_PATH.HOME : PUBLIC_PATH.ROOT}
          $logoSrc={isContactUsPage ? LogoSnapSvg : (isDevelopment ? LogoDevSvg : LogoSvg)}
          $isClickable={isLogoClickable}
          $isSnap={isContactUsPage}
          onClick={(e) => {
            if (!isLogoClickable) {
              e.preventDefault();
            }
          }}
        />
        <RightSection>
          <DropDown
            dropdownContentProps={languageDropdownContentProps}
            dropdownTrigger={
              <LanguageTrigger>
                <Icon iconSrc={GlobalSvg} iconSize={18} />
                <Typo typoType="btn3m" color="gray1">
                  {languageLabels[currentLanguage]}
                </Typo>
              </LanguageTrigger>
            }
          >
            <DropdownMenuContainer>
              {languageOptions.map((option) => (
                <StyledDropdownMenuItem
                  key={option.value}
                  onSelect={() => handleLanguageChange(option.value)}
                >
                  <Typo typoType="b9r" color="gray2">
                    {option.label}
                  </Typo>
                </StyledDropdownMenuItem>
              ))}
            </DropdownMenuContainer>
          </DropDown>
          {normalizedUser && (
            <DropDown
              dropdownContentProps={accountDropdownContentProps}
              dropdownTrigger={
                <AccountButton>
                  <Typo typoType="btn5m" color="gray1">
                    {userName}
                  </Typo>
                  <ChevronDownIcon />
                </AccountButton>
              }
            >
              <DropdownMenuContainer>
                <StyledDropdownMenuItem onSelect={handleLogout}>
                  <Icon iconSrc={LogoutSvg} iconSize={16} />
                  <Typo typoType="b9r" color="gray2">
                    {t("common:logout")}
                  </Typo>
                </StyledDropdownMenuItem>
              </DropdownMenuContainer>
            </DropDown>
          )}
        </RightSection>
      </HeaderContainer>
    </Header>
  );
};

export default PublicPageHeader;

const Header = styled.header`
  background: ${colorSet.white};
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.06);
`;

const HeaderContainer = styled.div`
  height: 80px;
  padding: 20px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1920px;
  width: 100%;
  margin: 0 auto;
`;

const Logo = styled(Link)<{ $logoSrc: string; $isClickable?: boolean; $isSnap?: boolean }>`
  width: ${({ $isSnap }) => ($isSnap ? "147px" : "160px")};
  height: ${({ $isSnap }) => ($isSnap ? "18px" : "21px")};
  display: inline-block;
  background: ${({ $logoSrc }) => `url(${$logoSrc}) center center no-repeat`};
  background-size: contain;
  cursor: ${({ $isClickable }) => ($isClickable !== false ? "pointer" : "default")};
  text-decoration: none;
  flex-shrink: 0;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
`;

const LanguageTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const AccountButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px 4px 12px;
  background: ${colorSet.white};
  border: 0.5px solid rgba(160, 164, 171, 0.2);
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(5, 29, 57, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colorSet.gray11};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const ChevronDownIcon = styled(ChevronDownSvg)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  path {
    fill: ${colorSet.gray1};
  }
`;

const DropdownMenuContainer = styled.div`
  min-width: 160px;
  background: ${colorSet.white};
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
`;

const StyledDropdownMenuItem = styled(DropdownMenuItem)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  cursor: pointer;
  outline: none;

  &:focus,
  &:hover {
    background: ${colorSet.gray11};
  }
`;
